import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const VideoHero = () => {
  const [isMusicOn, setIsMusicOn] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Preload video for better performance
    const preloadVideo = () => {
      if (videoRef.current) {
        videoRef.current.load();
      }
    };

    preloadVideo();
    
    // Set page title for better SEO
    document.title = "Laminance Cabinetry | Custom Kitchen Cabinets & Acrylic Cabinets";
    
    // Add meta description dynamically
    const metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    metaDescription.content = 'Custom kitchen cabinets, acrylic cabinets, and modern cabinetry solutions by Laminance Cabinetry. European-style kitchen cabinets with premium finishes and full customization.';
    document.head.appendChild(metaDescription);
    
    // Cleanup
    return () => {
      document.head.removeChild(metaDescription);
    };
  }, []);

  const toggleMusic = () => {
    const audio = audioRef.current;
    if (audio) {
      if (isMusicOn) {
        audio.pause();
      } else {
        audio.play().catch((err) => console.warn("Error playing audio:", err));
      }
      setIsMusicOn((prev) => !prev);
    }
  };

  const handleVideoLoad = () => {
    setIsLoaded(true);
    setVideoError(false);
  };

  const handleVideoError = () => {
    console.error("Video failed to load");
    setVideoError(true);
    setIsLoaded(true); // Still show content even if video fails
  };

  const handleGetFreeDesign = () => {
    navigate('/catalog');
  };

  const handleViewPortfolio = () => {
    navigate('/gallery');
  };

  // Schema data for structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CabinetStore",
    "@id": "https://laminance.com/#business",
    "name": "Laminance Cabinetry",
    "description": "Laminance Cabinetry specializes in custom kitchen cabinets, acrylic cabinets, and modern cabinetry solutions.",
    "url": "https://laminance.com",
    "telephone": "+18624506069",
    "email": "contactus@laminance.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "418 Rt 23",
      "addressLocality": "Franklin",
      "addressRegion": "NJ",
      "postalCode": "07416",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "41.1220",
      "longitude": "-74.5804"
    },
    "areaServed": {
      "@type": "State",
      "name": "New Jersey"
    },
    "knowsAbout": [
      "Custom Kitchen Cabinets",
      "Acrylic Cabinets",
      "Modern Cabinetry",
      "Cabinet Installation",
      "Kitchen Remodeling",
      "European Cabinets"
    ],
    "priceRange": "$$",
    "openingHours": "Mo-Fr 09:00-18:00",
    "image": [
      "https://laminance.com/assets/Images/mobile-video-poster.jpg",
      "https://laminance.com/assets/Images/logo.png"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "bestRating": "5",
      "ratingCount": "200"
    }
  };

  // Breadcrumb structured data
  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [{
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://laminance.com"
    }]
  };

  return (
    <section 
      className="relative h-screen w-full overflow-hidden bg-black" 
      role="banner" 
      aria-label="Laminance Cabinetry - Custom Kitchen Cabinets & Acrylic Cabinets"
      itemScope
      itemType="https://schema.org/CabinetStore"
    >
      {/* Structured Data for SEO - Multiple Schemas */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbData)}
      </script>

      {/* Additional Local Business Schema for better local SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Laminance Cabinetry",
          "image": "https://laminance.com/assets/Images/logo.png",
          "@id": "https://laminance.com",
          "url": "https://laminance.com",
          "telephone": "+18624506069",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "418 Rt 23",
            "addressLocality": "Franklin",
            "addressRegion": "NJ",
            "postalCode": "07416",
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "41.1220",
            "longitude": "-74.5804"
          },
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday"
            ],
            "opens": "09:00",
            "closes": "18:00"
          }
        })}
      </script>

      {/* Audio Player */}
      <audio 
        ref={audioRef} 
        loop 
        preload="auto" 
        aria-hidden="true"
        className="hidden"
      >
        <source src="/assets/Images/song.mp3" type="audio/mp3" />
        Your browser does not support the audio element.
      </audio>

      {/* Optimized Video Background - Single Video Source */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {/* Video Container with full coverage */}
        <div className="relative w-full h-full min-h-screen">
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
            className={`
              absolute min-w-full min-h-full w-auto h-auto
              object-cover object-center
              transition-opacity duration-500
              ${isLoaded ? 'opacity-100' : 'opacity-0'}
            `}
            style={{
              width: '100%',
              height: '100%',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)'
            }}
            onLoadedData={handleVideoLoad}
            onCanPlay={handleVideoLoad}
            onError={handleVideoError}
            title="Laminance Cabinetry Custom Kitchen Cabinet Installation Video"
            poster="/assets/Images/mobile-video-poster.jpg"
            aria-label="Video showcasing Laminance Cabinetry's custom kitchen cabinet installations and acrylic cabinet designs"
          >
            {/* Single optimized video source for all devices */}
            <source 
              src="/assets/Images/mobilevideo.mp4" 
              type="video/mp4" 
            />
            <source 
              src="/assets/Images/mobilevideo.webm" 
              type="video/webm" 
            />
            Your browser does not support the video tag.
          </video>
          
          {/* Fallback image if video fails to load */}
          {videoError && (
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: "url('/assets/Images/mobile-video-poster.jpg')"
              }}
              aria-hidden="true"
              role="img"
              aria-label="Custom kitchen cabinet installation by Laminance Cabinetry"
            />
          )}
        </div>
        
        {/* Enhanced Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-orange-900/20 to-black/80"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent md:bg-gradient-to-r md:from-black/70 md:via-black/40 md:to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex h-full items-center justify-center sm:justify-start">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 mt-16 ">
          <div 
            className={`w-full max-w-2xl transition-all duration-1000 text-center sm:text-left ${
              isLoaded ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
            }`}
            itemScope
            itemType="https://schema.org/Service"
          >
           

            <meta itemProp="serviceType" content="Custom Kitchen Cabinets" />
            <meta itemProp="provider" content="Laminance Cabinetry" />
            <meta itemProp="areaServed" content="New Jersey, USA" />
            <meta itemProp="mainEntityOfPage" content="https://laminance.com" />

            

            <div 
              className="mb-2 sm:mb-4 inline-flex items-center gap-2 text-sm sm:text-base text-orange-200/90 bg-black/30 rounded-full px-4 py-2 backdrop-blur-sm"
              itemProp="areaServed"
              itemScope
              itemType="https://schema.org/Place"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              <span itemProp="name">Serving New Jersey & Surrounding Areas</span>
            </div>

            

            <h1 
              className="mb-4 text-2xl xs:text-3xl sm:text-4xl md:text-4xl lg:text-6xl xl:text-7xl font-bold leading-tight text-white"
              itemProp="name"
            >
              Elegant + Quality ={' '}
              <span 
                className="bg-gradient-to-r from-orange-400 via-orange-300 to-yellow-300 bg-clip-text text-transparent block mt-2 sm:mt-0 sm:inline"
                itemProp="description"
              >
                Budget-Friendly
              </span>{' '}
              Premium Kitchen
            </h1>

            {/* SEO-Optimized Description */}
            <p 
              className="mb-6 sm:mb-8 max-w-xl text-sm sm:text-base lg:text-sm xl:text-lg text-gray-100 leading-relaxed backdrop-blur-sm bg-white/5 rounded-2xl p-4 sm:p-5 border border-white/10 mx-auto sm:mx-0"
              itemProp="description"
            >
              Laminance Cabinetry specializes in custom kitchen cabinets, acrylic cabinets, and modern cabinetry solutions. 
              We design and install European-style kitchen cabinets with premium finishes, smart storage, and full customization — 
              delivering elegance, durability, and exceptional value for your kitchen remodeling project.
            </p>

            

            {/* CTA Buttons - Centered on Mobile */}
            <div className="mb-8 sm:mb-12 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center sm:justify-start">
              <button 
                onClick={handleGetFreeDesign}
                className="group relative overflow-hidden rounded-full bg-gradient-to-r from-orange-500 to-orange-600 px-5 py-3 sm:px-6 sm:py-3 md:px-8 md:py-4 font-semibold text-white transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/30 focus:outline-none focus:ring-4 focus:ring-orange-500/50 text-sm sm:text-base"
                aria-label="Get Free Kitchen Cabinet Design Consultation - Redirects to Catalog Page"
                itemProp="potentialAction"
                itemScope
                itemType="https://schema.org/Action"
              >
                <meta itemProp="name" content="Get Free Cabinet Design" />
                <span className="relative z-10 flex items-center gap-2 justify-center">
                  Get Free Cabinet Design
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-orange-700 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
              </button>
              
              <button 
                onClick={handleViewPortfolio}
                className="group rounded-full border-2 border-white/40 bg-white/10 px-5 py-3 sm:px-6 sm:py-3 md:px-8 md:py-4 font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:bg-white hover:text-orange-600 hover:border-white hover:shadow-2xl hover:shadow-white/25 focus:outline-none focus:ring-4 focus:ring-white/50 text-sm sm:text-base"
                aria-label="View Our Kitchen Cabinet Portfolio Gallery - Redirects to Gallery Page"
                itemProp="potentialAction"
                itemScope
                itemType="https://schema.org/Action"
              >
                <meta itemProp="name" content="View Cabinet Portfolio" />
                <span className="relative z-10 flex items-center gap-2 justify-center">
                  View Cabinet Portfolio
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                </span>
              </button>
            </div>

            {/* SEO-Optimized Trust Indicators */}
            <div 
              className="flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-6 lg:gap-8"
              itemScope
              itemType="https://schema.org/AggregateRating"
            >
              <meta itemProp="ratingValue" content="5" />
              <meta itemProp="bestRating" content="5" />
              <meta itemProp="ratingCount" content="200" />
              <meta itemProp="itemReviewed" content="Laminance Cabinetry Custom Kitchen Cabinets" />
              
              {[
                { 
                  number: '200+', 
                  text: 'Custom Kitchen Cabinet ', 
                  icon: '🏠',
                  schema: 'numberOfKitchens',
                  ariaLabel: '200 plus custom kitchen cabinet projects completed'
                },
                { 
                  number: '300+', 
                  text: 'Cabinet Finish & Color ', 
                  icon: '🎨',
                  schema: 'colorOptions',
                  ariaLabel: '300 plus cabinet finish and color options available'
                },
                { 
                  number: '5★', 
                  text: 'Cabinet Customer Rating', 
                  icon: '⭐',
                  schema: 'ratingValue',
                  ariaLabel: 'Five star customer rating for cabinet installation'
                }
              ].map((item, index) => (
                <div 
                  key={index}
                  className={`transition-all duration-500 flex-1 min-w-[90px] xs:min-w-[100px] sm:min-w-[110px] md:min-w-0 ${
                    isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                  style={{ transitionDelay: `${800 + index * 200}ms` }}
                  itemProp={item.schema}
                  content={item.number.replace('+', '').replace('★', '')}
                  aria-label={item.ariaLabel}
                >
                  <div className="text-center backdrop-blur-sm bg-white/5 rounded-lg sm:rounded-xl md:rounded-2xl p-2 sm:p-3 md:p-4 border border-white/10 hover:border-orange-400/30 transition-all duration-300">
                    <div 
                      className="text-lg sm:text-xl md:text-2xl mb-1" 
                      role="img" 
                      aria-hidden="true"
                    >
                      {item.icon}
                    </div>
                    <div className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-orange-400">
                      {item.number}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-200 mt-1 leading-tight">
                      {item.text}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Music Toggle */}
      <button
        onClick={toggleMusic}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 z-20 flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-orange-500/20 backdrop-blur-sm border border-orange-500/30 transition-all duration-300 hover:scale-110 hover:bg-orange-500/30 hover:shadow-lg hover:shadow-orange-500/25 focus:outline-none focus:ring-4 focus:ring-orange-500/50"
        aria-label={isMusicOn ? "Turn off background music" : "Turn on background music for better browsing experience"}
        aria-pressed={isMusicOn}
        role="switch"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          className="h-5 w-5 sm:h-6 sm:w-6"
          fill={isMusicOn ? "#f97316" : "#ffffff"}
          aria-hidden="true"
        >
          {isMusicOn ? (
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
          ) : (
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
          )}
        </svg>
      </button>

      {/* Scroll Indicator */}
      

      {/* Decorative Elements */}
      <div className="absolute top-1/4 right-4 sm:right-10 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-orange-400 rounded-full animate-pulse" aria-hidden="true"></div>
      <div className="absolute bottom-1/3 left-3 sm:left-5 w-1 h-1 bg-orange-300 rounded-full animate-pulse delay-1000" aria-hidden="true"></div>
      <div className="absolute top-1/2 right-8 sm:right-20 w-1 h-1 bg-orange-200 rounded-full animate-pulse delay-500" aria-hidden="true"></div>
      
      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent" aria-hidden="true"></div>
    </section>
  );
};

export default VideoHero;











// import React, { useState, useRef, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';

// const VideoHero = () => {
//   const [isMusicOn, setIsMusicOn] = useState(false);
//   const [isLoaded, setIsLoaded] = useState(false);
//   const [videoError, setVideoError] = useState(false);
//   const audioRef = useRef(null);
//   const videoRef = useRef(null);
//   const navigate = useNavigate();

//   // Define color variables
//   const primaryColor = '#2a435d'; // Deep blue from your request
//   const secondaryColor = '#36597a'; // Lighter blue for accents
//   const whiteColor = '#ffffff';
//   const lightGray = '#f8f9fa';

//   useEffect(() => {
//     // Add Bell MT and Inter fonts
//     const style = document.createElement('style');
//     style.textContent = `
//       @import url('https://fonts.cdnfonts.com/css/bell-mt');
//       @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
      
//       .bell-mt-primary {
//         font-family: 'Bell MT', serif !important;
//       }
      
//       .inter-secondary {
//         font-family: 'Inter', sans-serif !important;
//       }
//     `;
//     document.head.appendChild(style);

//     // Preload video for better performance
//     const preloadVideo = () => {
//       if (videoRef.current) {
//         videoRef.current.load();
//       }
//     };

//     preloadVideo();
    
//     // Set page title for better SEO
//     document.title = "Laminance Cabinetry | Custom Kitchen Cabinets & Acrylic Cabinets";
    
//     // Add meta description dynamically
//     const metaDescription = document.createElement('meta');
//     metaDescription.name = 'description';
//     metaDescription.content = 'Custom kitchen cabinets, acrylic cabinets, and modern cabinetry solutions by Laminance Cabinetry. European-style kitchen cabinets with premium finishes and full customization.';
//     document.head.appendChild(metaDescription);
    
//     // Cleanup
//     return () => {
//       document.head.removeChild(metaDescription);
//       document.head.removeChild(style);
//     };
//   }, []);

//   const toggleMusic = () => {
//     const audio = audioRef.current;
//     if (audio) {
//       if (isMusicOn) {
//         audio.pause();
//       } else {
//         audio.play().catch((err) => console.warn("Error playing audio:", err));
//       }
//       setIsMusicOn((prev) => !prev);
//     }
//   };

//   const handleVideoLoad = () => {
//     setIsLoaded(true);
//     setVideoError(false);
//   };

//   const handleVideoError = () => {
//     console.error("Video failed to load");
//     setVideoError(true);
//     setIsLoaded(true); // Still show content even if video fails
//   };

//   const handleGetFreeDesign = () => {
//     navigate('/catalog');
//   };

//   const handleViewPortfolio = () => {
//     navigate('/gallery');
//   };

//   // Schema data for structured data
//   const structuredData = {
//     "@context": "https://schema.org",
//     "@type": "CabinetStore",
//     "@id": "https://laminance.com/#business",
//     "name": "Laminance Cabinetry",
//     "description": "Laminance Cabinetry specializes in custom kitchen cabinets, acrylic cabinets, and modern cabinetry solutions.",
//     "url": "https://laminance.com",
//     "telephone": "+18624506069",
//     "email": "contactus@laminance.com",
//     "address": {
//       "@type": "PostalAddress",
//       "streetAddress": "418 Rt 23",
//       "addressLocality": "Franklin",
//       "addressRegion": "NJ",
//       "postalCode": "07416",
//       "addressCountry": "US"
//     },
//     "geo": {
//       "@type": "GeoCoordinates",
//       "latitude": "41.1220",
//       "longitude": "-74.5804"
//     },
//     "areaServed": {
//       "@type": "State",
//       "name": "New Jersey"
//     },
//     "knowsAbout": [
//       "Custom Kitchen Cabinets",
//       "Acrylic Cabinets",
//       "Modern Cabinetry",
//       "Cabinet Installation",
//       "Kitchen Remodeling",
//       "European Cabinets"
//     ],
//     "priceRange": "$$",
//     "openingHours": "Mo-Fr 09:00-18:00",
//     "image": [
//       "https://laminance.com/assets/Images/mobile-video-poster.jpg",
//       "https://laminance.com/assets/Images/logo.png"
//     ],
//     "aggregateRating": {
//       "@type": "AggregateRating",
//       "ratingValue": "5",
//       "bestRating": "5",
//       "ratingCount": "200"
//     }
//   };

//   // Breadcrumb structured data
//   const breadcrumbData = {
//     "@context": "https://schema.org",
//     "@type": "BreadcrumbList",
//     "itemListElement": [{
//       "@type": "ListItem",
//       "position": 1,
//       "name": "Home",
//       "item": "https://laminance.com"
//     }]
//   };

//   return (
//     <section 
//       className="relative h-screen w-full overflow-hidden" 
//       style={{ backgroundColor: primaryColor }}
//       role="banner" 
//       aria-label="Laminance Cabinetry - Custom Kitchen Cabinets & Acrylic Cabinets"
//       itemScope
//       itemType="https://schema.org/CabinetStore"
//     >
//       {/* Structured Data for SEO - Multiple Schemas */}
//       <script type="application/ld+json">
//         {JSON.stringify(structuredData)}
//       </script>
      
//       <script type="application/ld+json">
//         {JSON.stringify(breadcrumbData)}
//       </script>

//       {/* Additional Local Business Schema for better local SEO */}
//       <script type="application/ld+json">
//         {JSON.stringify({
//           "@context": "https://schema.org",
//           "@type": "LocalBusiness",
//           "name": "Laminance Cabinetry",
//           "image": "https://laminance.com/assets/Images/logo.png",
//           "@id": "https://laminance.com",
//           "url": "https://laminance.com",
//           "telephone": "+18624506069",
//           "address": {
//             "@type": "PostalAddress",
//             "streetAddress": "418 Rt 23",
//             "addressLocality": "Franklin",
//             "addressRegion": "NJ",
//             "postalCode": "07416",
//             "addressCountry": "US"
//           },
//           "geo": {
//             "@type": "GeoCoordinates",
//             "latitude": "41.1220",
//             "longitude": "-74.5804"
//           },
//           "openingHoursSpecification": {
//             "@type": "OpeningHoursSpecification",
//             "dayOfWeek": [
//               "Monday",
//               "Tuesday",
//               "Wednesday",
//               "Thursday",
//               "Friday"
//             ],
//             "opens": "09:00",
//             "closes": "18:00"
//           }
//         })}
//       </script>

//       {/* Audio Player */}
//       <audio 
//         ref={audioRef} 
//         loop 
//         preload="auto" 
//         aria-hidden="true"
//         className="hidden"
//       >
//         <source src="/assets/Images/song.mp3" type="audio/mp3" />
//         Your browser does not support the audio element.
//       </audio>

//       {/* Optimized Video Background - Single Video Source */}
//       <div className="absolute inset-0 z-0 overflow-hidden">
//         {/* Video Container with full coverage */}
//         <div className="relative w-full h-full min-h-screen">
//           <video
//             ref={videoRef}
//             autoPlay
//             muted
//             loop
//             playsInline
//             preload="auto"
//             aria-hidden="true"
//             className={`
//               absolute min-w-full min-h-full w-auto h-auto
//               object-cover object-center
//               transition-opacity duration-500
//               ${isLoaded ? 'opacity-100' : 'opacity-0'}
//             `}
//             style={{
//               width: '100%',
//               height: '100%',
//               top: '50%',
//               left: '50%',
//               transform: 'translate(-50%, -50%)'
//             }}
//             onLoadedData={handleVideoLoad}
//             onCanPlay={handleVideoLoad}
//             onError={handleVideoError}
//             title="Laminance Cabinetry Custom Kitchen Cabinet Installation Video"
//             poster="/assets/Images/mobile-video-poster.jpg"
//             aria-label="Video showcasing Laminance Cabinetry's custom kitchen cabinet installations and acrylic cabinet designs"
//           >
//             {/* Single optimized video source for all devices */}
//             <source 
//               src="/assets/Images/mobilevideo.mp4" 
//               type="video/mp4" 
//             />
//             <source 
//               src="/assets/Images/mobilevideo.webm" 
//               type="video/webm" 
//             />
//             Your browser does not support the video tag.
//           </video>
          
//           {/* Fallback image if video fails to load */}
//           {videoError && (
//             <div 
//               className="absolute inset-0 bg-cover bg-center"
//               style={{
//                 backgroundImage: "url('/assets/Images/mobile-video-poster.jpg')"
//               }}
//               aria-hidden="true"
//               role="img"
//               aria-label="Custom kitchen cabinet installation by Laminance Cabinetry"
//             />
//           )}
//         </div>
        
//         {/* Enhanced Gradient Overlays using #2a435d color */}
//         <div 
//           className="absolute inset-0"
//           style={{
//             background: `linear-gradient(to bottom right, 
//               rgba(0, 0, 0, 0.7), 
//               ${primaryColor}40, 
//               rgba(0, 0, 0, 0.7)
//             )`
//           }}
//         ></div>
//         <div 
//           className="absolute inset-0 md:hidden"
//           style={{
//             background: `linear-gradient(to right, 
//               ${primaryColor}cc, 
//               transparent
//             )`
//           }}
//         ></div>
//         <div 
//           className="absolute inset-0 hidden md:block"
//           style={{
//             background: `linear-gradient(to right, 
//               ${primaryColor}cc, 
//               ${primaryColor}80, 
//               transparent
//             )`
//           }}
//         ></div>
//         <div 
//           className="absolute inset-0"
//           style={{
//             background: `linear-gradient(to top, 
//               ${primaryColor}aa, 
//               transparent 70%
//             )`
//           }}
//         ></div>
//       </div>

//       {/* Main Content */}
//       <div className="relative z-10 flex h-full items-center justify-center sm:justify-start">
//         <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 mt-12 md:mt-16">
//           <div 
//             className={`w-full max-w-2xl transition-all duration-1000 text-center sm:text-left ${
//               isLoaded ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
//             }`}
//             itemScope
//             itemType="https://schema.org/Service"
//           >
//             {/* Location Badge */}
//             <div 
//               className="inter-secondary mb-3 sm:mb-4 inline-flex items-center gap-2 text-xs sm:text-sm md:text-base font-medium text-white/95 rounded-full px-4 py-2 backdrop-blur-sm"
//               style={{ backgroundColor: `${primaryColor}cc` }}
//               itemProp="areaServed"
//               itemScope
//               itemType="https://schema.org/Place"
//             >
//               <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
//                 <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
//               </svg>
//               <span itemProp="name">Serving New Jersey & Surrounding Areas</span>
//             </div>

//             {/* Main Headline */}
//             <h1 
//               className="bell-mt-primary mb-4 text-3xl xs:text-4xl sm:text-5xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight tracking-tight"
//               style={{ color: whiteColor }}
//               itemProp="name"
//             >
//               Elegant + Quality ={' '}
//               <span 
//                 className="bell-mt-primary bg-gradient-to-r from-white via-gray-100 to-gray-200 bg-clip-text text-transparent block mt-2 sm:mt-0 sm:inline"
//                 itemProp="description"
//               >
//                 Budget-Friendly
//               </span>{' '}
//               Premium Kitchen
//             </h1>

//             {/* SEO-Optimized Description */}
//             <p 
//               className="inter-secondary mb-6 sm:mb-8 max-w-xl text-sm sm:text-base lg:text-lg xl:text-xl font-light leading-relaxed backdrop-blur-sm rounded-2xl p-4 sm:p-5 mx-auto sm:mx-0"
//               style={{ 
//                 color: lightGray,
//                 backgroundColor: `${whiteColor}08`,
//                 border: `1px solid ${whiteColor}15`
//               }}
//               itemProp="description"
//             >
//               Laminance Cabinetry specializes in custom kitchen cabinets, acrylic cabinets, and modern cabinetry solutions. 
//               We design and install European-style kitchen cabinets with premium finishes, smart storage, and full customization — 
//               delivering elegance, durability, and exceptional value for your kitchen remodeling project.
//             </p>

//             {/* CTA Buttons - Centered on Mobile */}
//             <div className="inter-secondary mb-8 sm:mb-12 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center sm:justify-start">
//               {/* Primary CTA Button */}
//               <button 
//                 onClick={handleGetFreeDesign}
//                 className="group relative overflow-hidden rounded-full px-5 py-3 sm:px-6 sm:py-3 md:px-7 md:py-3.5 lg:px-8 lg:py-4 font-semibold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] text-sm sm:text-base md:text-lg"
//                 style={{ 
//                   background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
//                   color: whiteColor,
//                   boxShadow: `0 4px 20px ${primaryColor}40, 0 0 0 1px ${secondaryColor}30`
//                 }}
//                 onMouseEnter={(e) => {
//                   e.currentTarget.style.boxShadow = `0 6px 25px ${primaryColor}60, 0 0 0 1px ${secondaryColor}50`;
//                   e.currentTarget.style.background = `linear-gradient(135deg, ${secondaryColor}, ${primaryColor})`;
//                 }}
//                 onMouseLeave={(e) => {
//                   e.currentTarget.style.boxShadow = `0 4px 20px ${primaryColor}40, 0 0 0 1px ${secondaryColor}30`;
//                   e.currentTarget.style.background = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;
//                 }}
//                 aria-label="Get Free Kitchen Cabinet Design Consultation - Redirects to Catalog Page"
//                 itemProp="potentialAction"
//                 itemScope
//                 itemType="https://schema.org/Action"
//               >
//                 <meta itemProp="name" content="Get Free Cabinet Design" />
//                 <span className="relative z-10 flex items-center gap-2 justify-center">
//                   Get Free Cabinet Design
//                   <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
//                     <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
//                   </svg>
//                 </span>
//               </button>
              
//               {/* Secondary CTA Button */}
//               <button 
//                 onClick={handleViewPortfolio}
//                 className="inter-secondary group rounded-full px-5 py-3 sm:px-6 sm:py-3 md:px-7 md:py-3.5 lg:px-8 lg:py-4 font-semibold backdrop-blur-sm transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] text-sm sm:text-base md:text-lg"
//                 style={{ 
//                   color: whiteColor,
//                   backgroundColor: `${whiteColor}0f`,
//                   border: `2px solid ${whiteColor}30`,
//                   boxShadow: `0 4px 15px ${primaryColor}20`
//                 }}
//                 onMouseEnter={(e) => {
//                   e.currentTarget.style.backgroundColor = whiteColor;
//                   e.currentTarget.style.color = primaryColor;
//                   e.currentTarget.style.borderColor = whiteColor;
//                   e.currentTarget.style.boxShadow = `0 6px 20px ${whiteColor}30`;
//                 }}
//                 onMouseLeave={(e) => {
//                   e.currentTarget.style.backgroundColor = `${whiteColor}0f`;
//                   e.currentTarget.style.color = whiteColor;
//                   e.currentTarget.style.borderColor = `${whiteColor}30`;
//                   e.currentTarget.style.boxShadow = `0 4px 15px ${primaryColor}20`;
//                 }}
//                 aria-label="View Our Kitchen Cabinet Portfolio Gallery - Redirects to Gallery Page"
//                 itemProp="potentialAction"
//                 itemScope
//                 itemType="https://schema.org/Action"
//               >
//                 <meta itemProp="name" content="View Cabinet Portfolio" />
//                 <span className="relative z-10 flex items-center gap-2 justify-center">
//                   View Cabinet Portfolio
//                   <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
//                     <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
//                     <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
//                   </svg>
//                 </span>
//               </button>
//             </div>

//             {/* Trust Indicators */}
//             <div 
//               className="inter-secondary flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-5 lg:gap-6"
//               itemScope
//               itemType="https://schema.org/AggregateRating"
//             >
//               <meta itemProp="ratingValue" content="5" />
//               <meta itemProp="bestRating" content="5" />
//               <meta itemProp="ratingCount" content="200" />
//               <meta itemProp="itemReviewed" content="Laminance Cabinetry Custom Kitchen Cabinets" />
              
//               {[
//                 { 
//                   number: '200+', 
//                   text: 'Custom Kitchen Cabinet Projects', 
//                   icon: '🏠',
//                   schema: 'numberOfKitchens',
//                   ariaLabel: '200 plus custom kitchen cabinet projects completed'
//                 },
//                 { 
//                   number: '300+', 
//                   text: 'Cabinet Finish & Color Options', 
//                   icon: '🎨',
//                   schema: 'colorOptions',
//                   ariaLabel: '300 plus cabinet finish and color options available'
//                 },
//                 { 
//                   number: '5★', 
//                   text: 'Customer Satisfaction Rating', 
//                   icon: '⭐',
//                   schema: 'ratingValue',
//                   ariaLabel: 'Five star customer rating for cabinet installation'
//                 }
//               ].map((item, index) => (
//                 <div 
//                   key={index}
//                   className={`transition-all duration-500 flex-1 min-w-[90px] xs:min-w-[100px] sm:min-w-[110px] md:min-w-[120px] ${
//                     isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
//                   }`}
//                   style={{ transitionDelay: `${800 + index * 200}ms` }}
//                   itemProp={item.schema}
//                   content={item.number.replace('+', '').replace('★', '')}
//                   aria-label={item.ariaLabel}
//                 >
//                   <div 
//                     className="text-center backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-4 border transition-all duration-300"
//                     style={{ 
//                       backgroundColor: `${whiteColor}08`,
//                       borderColor: `${whiteColor}15`
//                     }}
//                     onMouseEnter={(e) => {
//                       e.currentTarget.style.borderColor = `${whiteColor}40`;
//                       e.currentTarget.style.backgroundColor = `${whiteColor}12`;
//                       e.currentTarget.style.transform = 'translateY(-2px)';
//                     }}
//                     onMouseLeave={(e) => {
//                       e.currentTarget.style.borderColor = `${whiteColor}15`;
//                       e.currentTarget.style.backgroundColor = `${whiteColor}08`;
//                       e.currentTarget.style.transform = 'translateY(0)';
//                     }}
//                   >
//                     <div 
//                       className="text-lg sm:text-xl md:text-2xl mb-1 sm:mb-2" 
//                       role="img" 
//                       aria-hidden="true"
//                     >
//                       {item.icon}
//                     </div>
//                     <div 
//                       className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold"
//                       style={{ color: whiteColor }}
//                     >
//                       {item.number}
//                     </div>
//                     <div 
//                       className="text-[10px] xs:text-xs sm:text-sm mt-1 leading-tight font-medium"
//                       style={{ color: `${whiteColor}aa` }}
//                     >
//                       {item.text}
//                     </div>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Music Toggle Button */}
//       <button
//         onClick={toggleMusic}
//         className="inter-secondary absolute top-4 right-4 sm:top-6 sm:right-6 z-20 flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full backdrop-blur-sm border transition-all duration-300 hover:scale-110 active:scale-95 focus:outline-none"
//         style={{ 
//           backgroundColor: `${primaryColor}cc`,
//           borderColor: `${whiteColor}30`,
//           boxShadow: `0 4px 15px ${primaryColor}40`
//         }}
//         onMouseEnter={(e) => {
//           e.currentTarget.style.backgroundColor = `${whiteColor}15`;
//           e.currentTarget.style.borderColor = whiteColor;
//         }}
//         onMouseLeave={(e) => {
//           e.currentTarget.style.backgroundColor = `${primaryColor}cc`;
//           e.currentTarget.style.borderColor = `${whiteColor}30`;
//         }}
//         aria-label={isMusicOn ? "Turn off background music" : "Turn on background music for better browsing experience"}
//         aria-pressed={isMusicOn}
//         role="switch"
//       >
//         <svg 
//           xmlns="http://www.w3.org/2000/svg" 
//           viewBox="0 0 24 24" 
//           className="h-5 w-5 sm:h-6 sm:w-6"
//           fill={whiteColor}
//           aria-hidden="true"
//         >
//           {isMusicOn ? (
//             <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
//           ) : (
//             <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
//           )}
//         </svg>
//       </button>

//       {/* Decorative Elements */}
//       <div 
//         className="absolute top-1/4 right-4 sm:right-10 w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full animate-pulse" 
//         style={{ backgroundColor: `${whiteColor}cc` }}
//         aria-hidden="true"
//       ></div>
//       <div 
//         className="absolute bottom-1/3 left-3 sm:left-5 w-1 h-1 rounded-full animate-pulse delay-1000" 
//         style={{ backgroundColor: `${whiteColor}aa` }}
//         aria-hidden="true"
//       ></div>
//       <div 
//         className="absolute top-1/2 right-8 sm:right-20 w-1 h-1 rounded-full animate-pulse delay-500" 
//         style={{ backgroundColor: `${whiteColor}99` }}
//         aria-hidden="true"
//       ></div>
      
//       {/* Bottom Gradient */}
//       <div 
//         className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
//         style={{
//           background: `linear-gradient(to top, ${primaryColor}, transparent)`
//         }}
//         aria-hidden="true"
//       ></div>

//       {/* Scroll Indicator for Desktop */}
//       <div className="hidden lg:block absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
//         <div className="flex flex-col items-center">
//           <span className="inter-secondary text-xs text-white/60 mb-2">Scroll to explore</span>
//           <svg className="w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
//           </svg>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default VideoHero;





